import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { checkSerialExists, createInstrument, updateInstrument } from "@/Wist-blockchain/collections/instruments/instruments.service"
import { useState, useRef, type ChangeEvent } from "react"
import { Camera, X, ImagePlus, ShieldCheck, Info, Package, CheckCircle2, Plus, Eye, EyeOff } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import type { InstrumentForm, AdditionalEquipmentItem, CredentialItem } from "@/Wist-blockchain/collections/instruments/types"
import { OrganizationCombobox } from "@/components/organizations-selected/organization-selected"
import { BrandCombobox } from "@/components/brands-selected/brands-selected"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { uploadInstrumentImages } from "@/Wist-blockchain/collections/instruments/instruments-storage.service"
import { compressImage } from "@/Wist-blockchain/lib-blockchain/instrument-compress-images"
import { toast } from "react-toastify"
import { generateAndUploadQR } from "@/Wist-blockchain/lib-blockchain/instruments-QR-generation"
import { Link, useNavigate } from "@tanstack/react-router"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { instrumentSchema, type InstrumentSchemaErrors } from "@/Wist-blockchain/instruments/utils/instrumentSchema"

const MAX_IMAGES = 5
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"]
const currentYear = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: currentYear - 1950 + 1 }, (_, i) => currentYear - i)

const VOLTAGE_OPTIONS = ["24V", "110V", "120V", "220V", "240V", "380V", "440V", "600V"]
const CURRENT_OPTIONS = ["AC", "DC", "AC/DC"]
const EQUIPMENT_TYPE_OPTIONS = [
    { value: "farmaceutico", label: "Farmacéutico" },
    { value: "industrial", label: "Industrial" },
    { value: "medico", label: "Médico" },
    { value: "alimentos_bebidas", label: "Alimentos y Bebidas" },
    { value: "laboratorio", label: "Laboratorio" },
    { value: "mineria", label: "Minería" },
    { value: "construccion", label: "Construcción" },
    { value: "ambiental", label: "Ambiental" },
    { value: "otro", label: "Otro" },
]

const STATUS_OPTIONS = [
    { value: "operativo", label: "Operativo" },
    { value: "operativo_observaciones", label: "Operativo con observaciones" },
    { value: "fuera_servicio", label: "Fuera de servicio" },
]

const ESTADO_EQUIPO_OPTIONS = ["Operativo", "Falla", "Prestado", "Baja"]

const initialForm: InstrumentForm = {
    serialNumber: "",
    model: null,
    idOrganization: null,
    equipmentName: null,
    idBrand: null,
    manufactureYear: null,
    voltage: null,
    current: null,
    softwareVersion: null,
    equipmentDescription: null,
    additionalEquipment: [],
    equipmentType: null,
    status: "operativo",
    location: null,
    clientAssetCode: null,
    correoNotificacionResponsable: null,
    correoCliente: null,
    idFiles: [],
    idQrFile: null,
    credentials: [],
}

const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const AddInstrument = () => {
    const navigate = useNavigate()
    const [showCancelDialog, setShowCancelDialog] = useState(false)
    const [form, setForm] = useState<InstrumentForm>(initialForm)
    const [images, setImages] = useState<{ file: File; preview: string }[]>([])
    const [serialValid, setSerialValid] = useState<boolean | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [organizationId, setOrganizationId] = useState("")
    const [brandId, setBrandId] = useState("")

    const [equipmentItems, setEquipmentItems] = useState<AdditionalEquipmentItem[]>([])
    const [hasCredentials, setHasCredentials] = useState(false)
    const [credentials, setCredentials] = useState<CredentialItem[]>([{ username: "", password: "" }])
    const [showPasswords, setShowPasswords] = useState<boolean[]>([false])

    const fileInputRef = useRef<HTMLInputElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [isCompressing, setIsCompressing] = useState(false)
    const [serialDuplicated, setSerialDuplicated] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [fieldErrors, setFieldErrors] = useState<InstrumentSchemaErrors>({})
    const [isCheckingSerial, setIsCheckingSerial] = useState(false)

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target

        if (name === "serialNumber") {
            const trimmed = value.trimStart()
            setForm((prev) => ({ ...prev, serialNumber: trimmed }))

            const isLongEnough = trimmed.length >= 4
            setSerialValid(isLongEnough ? true : trimmed.length > 0 ? false : null)
            setSerialDuplicated(false)

            if (debounceRef.current) clearTimeout(debounceRef.current)

            if (isLongEnough) {
                setIsCheckingSerial(true)
                debounceRef.current = setTimeout(async () => {
                    const exists = await checkSerialExists(trimmed)
                    setIsCheckingSerial(false)
                    if (exists) {
                        setSerialDuplicated(true)
                        setSerialValid(false)
                    }
                }, 600)
            } else {
                setIsCheckingSerial(false)
            }
            return
        }

        setForm((prev) => ({ ...prev, [name]: value }))
    }

    const prepareImages = async (files: File[], remaining: number) => {
        const filtered = files.filter((f) => ACCEPTED_TYPES.includes(f.type)).slice(0, remaining)
        return Promise.all(
            filtered.map(async (file) => {
                const compressed = await compressImage(file)
                return { file: compressed, preview: URL.createObjectURL(compressed) }
            })
        )
    }

    const handleImageSelect = async (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? [])
        const remaining = MAX_IMAGES - images.length
        if (!files.length) return

        setIsCompressing(true)
        try {
            const prepared = await prepareImages(files, remaining)
            setImages((prev) => [...prev, ...prepared])
        } finally {
            setIsCompressing(false)
            if (fileInputRef.current) fileInputRef.current.value = ""
        }
    }

    const handleRemoveImage = (index: number) => {
        setImages((prev) => {
            URL.revokeObjectURL(prev[index].preview)
            return prev.filter((_, i) => i !== index)
        })
        setIsDragging(false)
    }

    const addEquipmentItem = () =>
        setEquipmentItems(prev => [...prev, { nombre: "", serial: "", modelo: "", estado: "", descripcion: "" }])

    const removeEquipmentItem = (index: number) =>
        setEquipmentItems(prev => prev.filter((_, i) => i !== index))

    const updateEquipmentItem = (index: number, field: keyof AdditionalEquipmentItem, value: string) =>
        setEquipmentItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))

    const addCredential = () => {
        setCredentials(prev => [...prev, { username: "", password: "" }])
        setShowPasswords(prev => [...prev, false])
    }
    const removeCredential = (index: number) => {
        setCredentials(prev => prev.filter((_, i) => i !== index))
        setShowPasswords(prev => prev.filter((_, i) => i !== index))
    }
    const updateCredential = (index: number, field: keyof CredentialItem, value: string) =>
        setCredentials(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))
    const toggleShowPassword = (index: number) =>
        setShowPasswords(prev => prev.map((v, i) => i === index ? !v : v))

    const handleSaveAndCertify = async () => {
        const result = instrumentSchema.safeParse({
            serialNumber: form.serialNumber,
            idOrganization: form.idOrganization ?? "",
            equipmentName: form.equipmentName?.trim() ?? "",
            idBrand: form.idBrand ?? "",
            model: form.model?.trim() ?? "",
            voltage: form.voltage ?? "",
            current: form.current ?? "",
            equipmentType: form.equipmentType ?? "",
        })

        const errors: InstrumentSchemaErrors = {}
        if (!result.success) {
            for (const issue of result.error.issues) {
                const field = issue.path[0] as keyof InstrumentSchemaErrors
                if (field && !errors[field]) errors[field] = issue.message
            }
        }
        if (serialDuplicated) errors.serialNumber = "Este número de serie ya está registrado"
        else if (serialValid !== true && form.serialNumber.length > 0) errors.serialNumber = "Mínimo 4 caracteres"

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            toast.error("Completa los campos obligatorios")
            return
        }
        setFieldErrors({})

        setIsSubmitting(true)
        try {
            const exists = await checkSerialExists(form.serialNumber)
            if (exists) {
                setSerialDuplicated(true)
                setSerialValid(false)
                toast.error("El número de serie ya está registrado")
                setIsSubmitting(false)
                return
            }

            const trimmedForm = {
                ...form,
                serialNumber: form.serialNumber.trim(),
                equipmentName: form.equipmentName?.trim() ?? null,
                model: form.model?.trim() ?? null,
                location: form.location?.trim() ?? null,
                clientAssetCode: form.clientAssetCode?.trim() ?? null,
                equipmentDescription: form.equipmentDescription?.trim() ?? null,
                correoNotificacionResponsable: form.correoNotificacionResponsable?.trim() ?? null,
                correoCliente: form.correoCliente?.trim() ?? null,
                additionalEquipment: equipmentItems.map(item => JSON.stringify(item)),
                credentials: hasCredentials
                    ? credentials.filter(c => c.username || c.password).map(c => JSON.stringify(c))
                    : [],
            }

            const fileIds = await uploadInstrumentImages(images.map((img) => img.file))
            const instrumentId = await createInstrument({ ...trimmedForm, idFiles: fileIds })
            const qrFileId = await generateAndUploadQR(instrumentId)
            await updateInstrument(instrumentId, { idQrFile: qrFileId })

            toast.success("Equipo registrado correctamente")

            setForm(initialForm)
            setImages([])
            setSerialValid(null)
            setSerialDuplicated(false)
            setOrganizationId("")
            setBrandId("")
            setEquipmentItems([])
            setHasCredentials(false)
            setCredentials([{ username: "", password: "" }])
            setShowPasswords([false])
            setFieldErrors({})
        } catch (error) {
            console.error(error)
            toast.error("Ocurrió un error al guardar el equipo")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <Package className="h-4 w-4" />
                        <BreadcrumbLink asChild>
                            <Link to="/instruments/listInstruments">Lista de Instrumentos</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>Registro de Nuevo Equipo</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="space-y-6 pt-6">

                <div>
                    <h1 className="text-2xl font-bold sm:text-3xl">Registro de Nuevo Equipo</h1>
                    <p className="mt-1 text-sm text-blue-500 dark:text-blue-400">
                        Asegúrese de que el número de serie coincida con la placa física.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

                    {/* ─── Columna izquierda: Información Técnica ─── */}
                    <div className="lg:col-span-2 space-y-4">
                        <Card className="rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
                            <h2 className="text-lg font-semibold">Información Técnica</h2>

                            {/* Organización */}
                            <div className="space-y-1.5">
                                <Label className={labelFormStyle}>Organización o Cliente <span className="text-red-500">*</span></Label>
                                <div>
                                    <OrganizationCombobox
                                        value={organizationId}
                                        onChange={(id) => {
                                            setOrganizationId(id)
                                            setForm(prev => ({ ...prev, idOrganization: id }))
                                        }}
                                    />
                                    {fieldErrors.idOrganization && (
                                        <p className="text-xs text-red-500">{fieldErrors.idOrganization}</p>
                                    )}
                                </div>
                            </div>

                            {/* Código de Serie */}
                            <div className="space-y-1.5">
                                <Label htmlFor="serialNumber" className={labelFormStyle}>
                                    Código de Serie (S/N) <span className="text-red-500">*</span>
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="serialNumber"
                                        name="serialNumber"
                                        placeholder="SN-8829-XJ-001"
                                        value={form.serialNumber}
                                        onChange={handleChange}
                                        className={
                                            isCheckingSerial
                                                ? "border-gray-300 pr-9"
                                                : serialValid === true
                                                    ? "border-emerald-400 pr-9"
                                                    : serialValid === false
                                                        ? "border-red-400 pr-9"
                                                        : "pr-9"
                                        }
                                    />
                                    {isCheckingSerial && (
                                        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-gray-300 border-t-blue-500 animate-spin" />
                                    )}
                                    {!isCheckingSerial && serialValid === true && (
                                        <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                                    )}
                                </div>
                                {serialValid === false && (
                                    <p className="text-xs text-red-500">
                                        {serialDuplicated ? "Este número de serie ya está registrado" : "Mínimo 4 caracteres"}
                                    </p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label className={labelFormStyle}>Tipo de Equipo <span className="text-red-500">*</span></Label>
                                    <Select
                                        onValueChange={(value) => setForm(prev => ({ ...prev, equipmentType: value }))}
                                        value={form.equipmentType ?? ""}
                                    >
                                        <SelectTrigger className={`w-full ${fieldErrors.equipmentType ? "border-red-400" : ""}`}>
                                            <SelectValue placeholder="Seleccionar tipo..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {EQUIPMENT_TYPE_OPTIONS.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {fieldErrors.equipmentType && (
                                        <p className="text-xs text-red-500">{fieldErrors.equipmentType}</p>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <Label className={labelFormStyle}>Código activo del cliente</Label>
                                    <Input
                                        id="clientAssetCode"
                                        name="clientAssetCode"
                                        placeholder="SN-0000-1234-001"
                                        value={form.clientAssetCode ?? ""}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="location" className={labelFormStyle}>Ubicación</Label>
                                    <Input
                                        id="location"
                                        name="location"
                                        placeholder="Ej: Piso 2, Sala de laboratorio"
                                        value={form.location ?? ""}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="correoNotificacionResponsable" className={labelFormStyle}>
                                        Correo técnico a notificar
                                    </Label>
                                    <Input
                                        id="correoNotificacionResponsable"
                                        name="correoNotificacionResponsable"
                                        type="email"
                                        placeholder="tecnico@wist-ec.com"
                                        value={form.correoNotificacionResponsable ?? ""}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="correoCliente" className={labelFormStyle}>Correo cliente a notificar</Label>
                                    <Input
                                        id="correoCliente"
                                        name="correoCliente"
                                        type="email"
                                        placeholder="cliente@empresa.com"
                                        value={form.correoCliente ?? ""}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <Label className={labelFormStyle}>Estado</Label>
                                    <Select
                                        onValueChange={(value) => setForm(prev => ({ ...prev, status: value }))}
                                        value={form.status ?? ""}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Seleccionar estado..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((s) => (
                                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label className={labelFormStyle}>Marca fabricante <span className="text-red-500">*</span></Label>
                                    <div>
                                        <BrandCombobox
                                            value={brandId}
                                            onChange={(id) => {
                                                setBrandId(id)
                                                setForm(prev => ({ ...prev, idBrand: id }))
                                            }}
                                        />
                                        {fieldErrors.idBrand && (
                                            <p className="text-xs text-red-500">{fieldErrors.idBrand}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="equipmentName" className={labelFormStyle}>
                                        Nombre del equipo <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        id="equipmentName"
                                        name="equipmentName"
                                        placeholder="Ej: Disolutor"
                                        value={form.equipmentName ?? ""}
                                        onChange={handleChange}
                                        className={fieldErrors.equipmentName ? "border-red-400" : ""}
                                    />
                                    {fieldErrors.equipmentName && (
                                        <p className="text-xs text-red-500">{fieldErrors.equipmentName}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label htmlFor="model" className={labelFormStyle}>Modelo <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="model"
                                        name="model"
                                        placeholder="Ej: RX-500 Turbo"
                                        value={form.model ?? ""}
                                        onChange={handleChange}
                                        className={fieldErrors.model ? "border-red-400" : ""}
                                    />
                                    {fieldErrors.model && (
                                        <p className="text-xs text-red-500">{fieldErrors.model}</p>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="manufactureYear" className={labelFormStyle}>Año de Fabricación</Label>
                                    <Select
                                        onValueChange={(value) => setForm(prev => ({ ...prev, manufactureYear: Number(value) }))}
                                        value={form.manufactureYear?.toString() ?? ""}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Seleccionar año..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {YEAR_OPTIONS.map((year) => (
                                                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <Label className={labelFormStyle}>Voltaje <span className="text-red-500">*</span></Label>
                                    <Select
                                        onValueChange={(value) => setForm(prev => ({ ...prev, voltage: value }))}
                                        value={form.voltage ?? ""}
                                    >
                                        <SelectTrigger className={`w-full ${fieldErrors.voltage ? "border-red-400" : ""}`}>
                                            <SelectValue placeholder="Seleccionar voltaje..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VOLTAGE_OPTIONS.map((voltage) => (
                                                <SelectItem key={voltage} value={voltage}>{voltage}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {fieldErrors.voltage && (
                                        <p className="text-xs text-red-500">{fieldErrors.voltage}</p>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <Label className={labelFormStyle}>Tipo de Corriente <span className="text-red-500">*</span></Label>
                                    <Select
                                        onValueChange={(value) => setForm(prev => ({ ...prev, current: value }))}
                                        value={form.current ?? ""}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Seleccionar corriente..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CURRENT_OPTIONS.map((current) => (
                                                <SelectItem key={current} value={current}>{current}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {fieldErrors.current && (
                                        <p className="text-xs text-red-500">{fieldErrors.current}</p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <Label className={labelFormStyle}>Versión de Software</Label>
                                <Input
                                    name="softwareVersion"
                                    placeholder="Ej: v2.4.1, Firmware 3.0..."
                                    value={form.softwareVersion ?? ""}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="observations" className={labelFormStyle}>Descripción del equipo</Label>
                                <Textarea
                                    id="observations"
                                    placeholder="Estado general del equipo, componentes adicionales, o notas de recepción..."
                                    rows={4}
                                    name="equipmentDescription"
                                    value={form.equipmentDescription ?? ""}
                                    onChange={handleChange}
                                    className="resize-none"
                                />
                            </div>
                        </Card>

                        {/* ─── Equipos adicionales ─── */}
                        <Card className="rounded-xl border border-gray-200 p-6 shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                                <h2 className="text-base font-semibold">Equipos adicionales</h2>
                                <Button type="button" variant="outline" size="sm" onClick={addEquipmentItem} className="gap-1">
                                    <Plus className="h-3.5 w-3.5" /> Agregar
                                </Button>
                            </div>
                            {equipmentItems.length === 0 && (
                                <p className="text-xs text-muted-foreground">No hay equipos adicionales registrados.</p>
                            )}
                            {equipmentItems.map((item, i) => (
                                <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-muted-foreground">Equipo {i + 1}</span>
                                        <button type="button" onClick={() => removeEquipmentItem(i)} className="text-red-500 hover:text-red-600">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Nombre</Label>
                                            <Input
                                                value={item.nombre}
                                                onChange={e => updateEquipmentItem(i, "nombre", e.target.value)}
                                                placeholder="Nombre del equipo"
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Serial</Label>
                                            <Input
                                                value={item.serial}
                                                onChange={e => updateEquipmentItem(i, "serial", e.target.value)}
                                                placeholder="Número de serie"
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Modelo</Label>
                                            <Input
                                                value={item.modelo}
                                                onChange={e => updateEquipmentItem(i, "modelo", e.target.value)}
                                                placeholder="Modelo"
                                                className="h-8 text-sm"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Estado</Label>
                                            <Select value={item.estado} onValueChange={v => updateEquipmentItem(i, "estado", v)}>
                                                <SelectTrigger className="h-8 text-sm">
                                                    <SelectValue placeholder="Estado..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ESTADO_EQUIPO_OPTIONS.map(e => (
                                                        <SelectItem key={e} value={e}>{e}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs text-muted-foreground">Descripción</Label>
                                        <Input
                                            value={item.descripcion}
                                            onChange={e => updateEquipmentItem(i, "descripcion", e.target.value)}
                                            placeholder="Descripción opcional"
                                            className="h-8 text-sm"
                                        />
                                    </div>
                                </div>
                            ))}
                        </Card>

                        {/* ─── Credenciales ─── */}
                        <Card className="rounded-xl border border-gray-200 p-6 shadow-sm space-y-3">
                            <div className="flex items-center justify-between">
                                <h2 className="text-base font-semibold">¿Tiene contraseña?</h2>
                                <Switch checked={hasCredentials} onCheckedChange={setHasCredentials} />
                            </div>
                            {hasCredentials && (
                                <div className="space-y-3">
                                    {credentials.map((cred, i) => (
                                        <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-semibold text-muted-foreground">Credencial {i + 1}</span>
                                                {credentials.length > 1 && (
                                                    <button type="button" onClick={() => removeCredential(i)} className="text-red-500 hover:text-red-600">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-muted-foreground">Usuario</Label>
                                                <Input
                                                    value={cred.username}
                                                    onChange={e => updateCredential(i, "username", e.target.value)}
                                                    placeholder=""
                                                    className="h-8 text-sm"
                                                />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs text-muted-foreground">Contraseña</Label>
                                                <div className="relative">
                                                    <Input
                                                        type={showPasswords[i] ? "text" : "password"}
                                                        value={cred.password}
                                                        onChange={e => updateCredential(i, "password", e.target.value)}
                                                        placeholder=""
                                                        className="h-8 text-sm pr-8"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleShowPassword(i)}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                                                    >
                                                        {showPasswords[i] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <Button type="button" variant="outline" size="sm" onClick={addCredential} className="gap-1">
                                        <Plus className="h-3.5 w-3.5" /> Agregar credencial
                                    </Button>
                                </div>
                            )}
                        </Card>
                    </div>

                    {/* ─── Columna derecha: Foto + Acciones ─── */}
                    <div className="space-y-5 lg:col-span-1">

                        {/* Card Fotografía */}
                        <Card className="rounded-xl border p-5 shadow-sm space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-sm font-semibold">Fotografía del Equipo</h2>
                                <span className="text-xs text-gray-400">{images.length}/{MAX_IMAGES}</span>
                            </div>

                            {images.length < MAX_IMAGES && (
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                                    onDragLeave={(e) => {
                                        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false)
                                    }}
                                    onDrop={async (e) => {
                                        e.preventDefault()
                                        setIsDragging(false)
                                        const files = Array.from(e.dataTransfer.files)
                                        const remaining = MAX_IMAGES - images.length
                                        if (!files.length) return
                                        setIsCompressing(true)
                                        try {
                                            const prepared = await prepareImages(files, remaining)
                                            setImages((prev) => [...prev, ...prepared])
                                        } finally {
                                            setIsCompressing(false)
                                        }
                                    }}
                                    onClick={() => fileInputRef.current?.click()}
                                    className={`w-full rounded-lg border-2 border-dashed transition-colors py-8 flex flex-col items-center gap-2 cursor-pointer
                                        ${isDragging
                                            ? "border-blue-400 bg-blue-50 text-blue-500 scale-[1.01]"
                                            : "border-gray-200 text-gray-400 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-500 dark:hover:bg-blue-900"
                                        }`}
                                >
                                    {isCompressing ? (
                                        <>
                                            <div className="h-8 w-8 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
                                            <span className="text-sm font-medium">Comprimiendo imagen...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Camera className={`h-8 w-8 transition-transform ${isDragging ? "scale-110" : ""}`} />
                                            <span className="text-sm font-medium">
                                                {isDragging ? "Suelta aquí..." : "Subir o arrastra una imagen"}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_TYPES.join(",")}
                                multiple
                                className="hidden"
                                onChange={handleImageSelect}
                            />

                            {images.length > 0 && (
                                <div className="space-y-2">
                                    {images.map((img, i) => (
                                        <div key={i} className="rounded-lg border border-gray-200 overflow-hidden">
                                            <div className="relative group">
                                                <img
                                                    src={img.preview}
                                                    alt={`Imagen ${i + 1}`}
                                                    className="h-24 w-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveImage(i)}
                                                        className="self-end flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                    <p className="text-white text-[10px] truncate">{formatFileSize(img.file.size)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {images.length < MAX_IMAGES && (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full h-16 rounded-lg border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-blue-500"
                                        >
                                            <ImagePlus className="h-5 w-5" />
                                            <span className="text-xs">Agregar</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </Card>

                        {/* ─── Acciones ─── */}
                        <Card className="rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
                            <div className="flex items-start gap-2 text-xs text-gray-400">
                                <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                                <p>Al registrar el equipo no podrá eliminarlo posteriormente.</p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <Button
                                    type="button"
                                    className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                                    onClick={handleSaveAndCertify}
                                    disabled={isSubmitting || isCompressing}
                                >
                                    <ShieldCheck className="h-4 w-4" />
                                    {isSubmitting ? "Guardando..." : "Guardar equipo"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full cursor-pointer"
                                    onClick={() => {
                                        const isDirty =
                                            form.serialNumber.trim() !== "" ||
                                            form.equipmentName !== null ||
                                            form.model !== null ||
                                            form.idOrganization !== null ||
                                            form.idBrand !== null ||
                                            form.location !== null ||
                                            form.clientAssetCode !== null ||
                                            form.equipmentDescription !== null ||
                                            equipmentItems.length > 0 ||
                                            images.length > 0
                                        if (isDirty) setShowCancelDialog(true)
                                        else navigate({ to: '/instruments/listInstruments' })
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </Button>
                            </div>
                        </Card>

                        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>¿Cancelar el registro?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Los datos ingresados se perderán. ¿Deseas salir sin guardar?
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel className="cursor-pointer">Seguir editando</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() => navigate({ to: '/instruments/listInstruments' })}
                                        className="cursor-pointer bg-destructive hover:bg-destructive/90 text-white"
                                    >
                                        Salir sin guardar
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>
            </div>
        </>
    )
}

const labelFormStyle = "text-sm text-gray-700 dark:text-gray-400 font-bold"

export default AddInstrument
